import type { ComputedRef, Ref } from 'vue'
import type { NativeSelectOption } from '@shared/ui/fields'

import { useCustomerStore } from '@entities/customer'
import { useListsStore } from '@shared/model'
import { useToast } from '@shared/ui/toast'

import { getKidUrl } from './list/customer-links'
import { useCustomerKidStoreApi } from '../api/kid-store.hook'

const DEFAULT_GRADE = 'K1'
const DEFAULT_DISCIPLINE = 'MATH'

interface ModalCustomerKidCreateReturn {
  refIsOpen: ComputedRef<boolean>
  refIsLoading: ComputedRef<boolean>
  refName: Ref<string>
  refIsNameInvalid: Ref<boolean>
  gradeOptions: ComputedRef<NativeSelectOption[]>
  disciplineOptions: ComputedRef<NativeSelectOption[]>
  setName: (value: string | number) => void
  setGrade: (option: NativeSelectOption) => void
  setDiscipline: (option: NativeSelectOption) => void
  closeModal: () => void
  submit: () => Promise<void>
}

const toOptions = (source: Record<string, string>, selected: string): NativeSelectOption[] =>
  Object.entries(source).map(([value, text]) => ({ id: value, value, text, selected: value === selected }))

/** "Add new kid": creates the kid with its first discipline and, like the legacy page, opens the new kid. */
export const useModalCustomerKidCreate = (): ModalCustomerKidCreateReturn => {
  const route = useRoute()
  const toast = useToast()
  const customerStore = useCustomerStore()
  const listsStore = useListsStore()

  const refName = ref<string>('')
  const refGrade = ref<string>(DEFAULT_GRADE)
  const refDiscipline = ref<string>(DEFAULT_DISCIPLINE)
  const refIsNameInvalid = ref<boolean>(false)

  const refIsOpen = computed<boolean>(() => customerStore.isModalKidCreate)
  const refIsLoading = computed<boolean>(() => customerStore.isProcessing)

  const gradeOptions = computed<NativeSelectOption[]>(() =>
    toOptions(listsStore.getLists?.kid?.grade ?? {}, refGrade.value))
  const disciplineOptions = computed<NativeSelectOption[]>(() =>
    toOptions(listsStore.getLists?.discipline?.code ?? {}, refDiscipline.value))

  const reset = (): void => {
    refName.value = ''
    refGrade.value = DEFAULT_GRADE
    refDiscipline.value = DEFAULT_DISCIPLINE
    refIsNameInvalid.value = false
  }

  const setName = (value: string | number): void => {
    refName.value = String(value)
    refIsNameInvalid.value = false
  }

  const setGrade = (option: NativeSelectOption): void => {
    refGrade.value = String(option.value ?? DEFAULT_GRADE)
  }

  const setDiscipline = (option: NativeSelectOption): void => {
    refDiscipline.value = String(option.value ?? DEFAULT_DISCIPLINE)
  }

  const closeModal = (): void => {
    if (customerStore.isProcessing) return

    customerStore.setModalKidCreate(false)
    reset()
  }

  const submit = async (): Promise<void> => {
    const name = refName.value.trim()

    if (!name) {
      refIsNameInvalid.value = true
      toast.warning({ title: 'Warning', description: 'You need to fill all the required fields.' })
      return
    }

    const kid = await useCustomerKidStoreApi({
      customer_id: Number(route.params.id),
      name,
      grade: refGrade.value,
      discipline_code: refDiscipline.value,
    })

    if (!kid) return

    toast.success({ title: 'Successfully added!', description: 'Kid was successfully added!' })
    customerStore.setModalKidCreate(false)
    reset()
    await navigateTo(getKidUrl(kid.id))
  }

  return {
    refIsOpen,
    refIsLoading,
    refName,
    refIsNameInvalid,
    gradeOptions,
    disciplineOptions,
    setName,
    setGrade,
    setDiscipline,
    closeModal,
    submit,
  }
}
